// src/contexts/ModalContext.jsx

import React, { createContext, useContext, useState, useCallback } from "react";
import ModalManager from "@/contexts/ModalManager"; // ⬅️ Central outlet for ALL modal rendering

/**
 * ┌─────────────────────────────────────────────────────────────┐
 * │           Zelospace Global Modal Context (v2)              │
 * └─────────────────────────────────────────────────────────────┘
 *
 * - Manages modal stack at the app root.
 * - Eliminates scattered useState/isOpen logic throughout the app.
 * - All modal opening/closing flows through this context.
 * - ModalManager renders actual modal components at the end of <body>.
 *
 * This is a scalable, Series-A ready solution that enables:
 *   - One source of truth for modal state.
 *   - Full decoupling of modal triggers and modal rendering.
 *   - "Teleportation" of modal UIs out of deep trees.
 *
 * Future engineers: All new modals must register with ModalManager.
 */

// 1️⃣ Create the modal context instance.
const ModalContext = createContext();

/**
 * 2️⃣ ModalProvider:
 *    - Wraps your entire app (see App.jsx).
 *    - Owns modalType (string: which modal is open)
 *    - Owns modalProps (object: props to pass to open modal)
 *    - openModal(type, props): opens modal globally
 *    - closeModal(): closes any open modal
 *    - Renders <ModalManager /> globally (this is where all modals are actually mounted)
 */
export const ModalProvider = ({ children }) => {
  const [modalType, setModalType] = useState(null);    // e.g. 'SUBMISSION_PREVIEW'
  const [modalProps, setModalProps] = useState({});    // data/handlers for modal

  // 🟢 Open any modal with a type string and (optional) props
  const openModal = useCallback((type, props = {}) => {
    setModalType(type);
    setModalProps(props);
  }, []);

  // 🔴 Close whatever modal is currently open (reset state)
  const closeModal = useCallback(() => {
    setModalType(null);
    setModalProps({});
  }, []);

  return (
    <ModalContext.Provider
      value={{
        modalType,    // Which modal is currently open (string or null)
        modalProps,   // Props/data passed to the modal instance
        openModal,    // Function to open a modal globally
        closeModal,   // Function to close modal
      }}
    >
      {/* 
        🧱 Renders global modal "portal" at root of app.
        All modal UIs in the app will mount here, using modalType as the selector.
        This ensures modals render OUTSIDE the component tree that triggers them.
        See ModalManager.jsx for registry of all modals.
      */}
      <ModalManager />
      {/* 
        The rest of your application.
        Child components can trigger modals by calling useModal().openModal().
      */}
      {children}
    </ModalContext.Provider>
  );
};

/**
 * 3️⃣ useModal():
 *    - Use this in any child component to access global modal state/controls
 *    - e.g. const { openModal, closeModal, modalType, modalProps } = useModal();
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};